/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Lets the v2 header's New menu open a list page's own create dialog:
 * /leads?create=1 calls the page's existing handler (the one its Add button
 * uses) once the page is ready, then drops the parameter so a refresh or Back
 * does not open it again. The page's own permission checks still apply: pass
 * `allowed` as the same condition that shows its Add button.
 *
 * v2 only: the classic look ignores the parameter.
 */

import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";

export const CREATE_PARAM = "create";

export function wantsCreate(search) {
  return new URLSearchParams(search || "").get(CREATE_PARAM) === "1";
}

export function withoutCreate(search) {
  const params = new URLSearchParams(search || "");
  params.delete(CREATE_PARAM);
  const rest = params.toString();
  return rest ? `?${rest}` : "";
}

export default function useOpenCreateFromUrl(open, { ready = true, allowed = true } = {}) {
  const v2 = useTheme()?.pearlUi === "v2";
  const { pathname, search, hash } = useLocation();
  const navigate = useNavigate();
  const openRef = useRef(open);
  openRef.current = open;

  useEffect(() => {
    if (!v2 || !ready || !wantsCreate(search)) return;
    navigate({ pathname, search: withoutCreate(search), hash }, { replace: true });
    if (allowed) openRef.current?.();
  }, [v2, ready, allowed, pathname, search, hash, navigate]);
}
